import {CodeAndExchange, Securities} from "../../../instruments/types/CodeAndExchange";
import {Observable} from "rxjs";
import {NewsHeadlineRecords} from "../../models/NewsHeadlineRecord";
import {DateTimeUtil} from "../../../js/util/DateTimeUtil";
import {NewsItemService} from "../items/NewsItemService";
import {NewsHeadlineStreamStore} from "../../store/stream/NewsHeadlineStreamStore";
import {buildQuoteRequest} from "../../../quote/util/QuoteStoreUtil";
import {QuoteStore} from "../../../quote/store/QuoteStore";
import {SecurityMap, SecurityMapOf} from "../../../security/common/map/SecurityMap";
import {buildHeadlineModels, NewsHeadlineIndicatorModel, NoNews} from "../../models/NewsHeadlineIndicatorModel";
import {currentHeadlinesOnly} from "./util/NewsHeadlineSourceFactoryUtil";

export class NewsHeadlineIndicatorSourceFactory {

    public static readonly id = "newsHeadlineIndicatorSourceFactory";

    /** @ngInject */
    public constructor(private readonly newsItemService: NewsItemService,
                       private readonly newsHeadlineStreamStore: NewsHeadlineStreamStore,
                       private readonly quoteStore: QuoteStore) {
    }

    public indicator(security: CodeAndExchange): Observable<NewsHeadlineIndicatorModel> {
        return this.indicators([security])
                   .map((models) => models.get(security))
                   .map((model) => model ?? NoNews(security))
                   .publishReplay(1)
                   .refCount();
    }

    public indicators(securities: Securities): Observable<SecurityMap<NewsHeadlineIndicatorModel>> {
        const headlines$ = this.currentHeadlines(securities);
        const quoteRequests = securities.map((s) => buildQuoteRequest(s));
        const quotes$ = this.quoteStore.scan(quoteRequests);
        const source = Observable.combineLatest(headlines$, quotes$);

        return source.auditTime(1000)
                     .map(([headlines, quotes]) => buildHeadlineModels(securities, headlines, quotes))
                     .publishReplay(1)
                     .refCount();
    }

    private currentHeadlines(securities: Securities): Observable<SecurityMap<NewsHeadlineRecords>> {
        const headlines$ = this.headlines(securities);
        return headlines$.scan((acc, next) => currentHeadlinesOnly(acc, next), SecurityMapOf<NewsHeadlineRecords>());
    }

    private headlines(securities: Securities): Observable<NewsHeadlineRecords> {
        const currentHeadlines = this.newsItemService.headlineHistory(securities, DateTimeUtil.offsetNow(), 1);
        const currentHeadlines$ = Observable.fromPromise(currentHeadlines)
                                            .catch(() => Observable.of([]));

        return currentHeadlines$.switchMap((headlines) => {
            const headlines$ = Observable.from(headlines);
            const updates$ = this.newsHeadlineStreamStore.headlines(securities);
            return Observable.concat(headlines$, updates$)
                             .bufferTime(1000);
        });
    }
}
